import { graphql, useStaticQuery } from 'gatsby';
import { APP_PREFIX } from './config';
import { setCookie, throwParamsToLink } from './helpers';

export const IS_SERVER = typeof window === 'undefined';
export const API_URL =
  process.env.NODE_ENV === 'production' ? !IS_SERVER && window.location.origin : '';

/**
 * Найти текущий лендинг в дериктории (используется для подключения стилей)
 * @param {String} locationPath расположение лендига (из стора)
 * @returns {Boolean} true - лендинг существует, false - лендинг не существует
 * @constructor
 */
export const RequireCSS = (locationPath) => {
  const data = useStaticQuery(graphql`
    {
      allSitePage {
        nodes {
          path
        }
      }
    }
  `);
  const pagesArr = data.allSitePage.nodes.map(({ path }) => path);
  return pagesArr.find((i) => i === locationPath);
};

/**
 * Ключи к иконкам платежек
 * @type {{perfectmoney: string, cryptopay: string, alfabank: string, tether: string, mastercard: string, mobilepay: string, ethereum: string, yandex: string, ecopayz: string, visa: string, mir: string, piastrix: string, webmoney: string, youmoney: string, bitcoin: string, qiwi: string}}
 */
export const PAY_ICONS = {
  alfabank: `${APP_PREFIX}/img/paymentIcons/alfabank.png`,
  bitcoin: `${APP_PREFIX}/img/paymentIcons/bitcoin.png`,
  cryptopay: `${APP_PREFIX}/img/paymentIcons/cryptopay.png`,
  ecopayz: `${APP_PREFIX}/img/paymentIcons/ecopayz.png`,
  ethereum: `${APP_PREFIX}/img/paymentIcons/ethereum.png`,
  mastercard: `${APP_PREFIX}/img/paymentIcons/mastercard.png`,
  mir: `${APP_PREFIX}/img/paymentIcons/mir.png`,
  mobilepay: `${APP_PREFIX}/img/paymentIcons/mobilepay.png`,
  perfectmoney: `${APP_PREFIX}/img/paymentIcons/perfectmoney.png`,
  piastrix: `${APP_PREFIX}/img/paymentIcons/piastrix.png`,
  qiwi: `${APP_PREFIX}/img/paymentIcons/qiwi.png`,
  tether: `${APP_PREFIX}/img/paymentIcons/tether.png`,
  visa: `${APP_PREFIX}/img/paymentIcons/visa.png`,
  webmoney: `${APP_PREFIX}/img/paymentIcons/webmoney.png`,
  yandex: `${APP_PREFIX}/img/paymentIcons/yandexmoney.png`,
  youmoney: `${APP_PREFIX}/img/paymentIcons/youmoney.png`,
};

/**
 * Объект с брендами
 * @type {{zenit: string, zenbet: string}}
 */
export const BRANDS = {
  zenit: 'zenit',
  zenbet: 'zenbet',
  cupis: 'cupis',
};

/**
 * Google Tag Manager
 * @type {{zenit: string, zenbet: string}}
 */
export const GTM = {
  zenit: 'GTM-5BSJV7F',
  zenbet: 'GTM-NR8JVD9',
  cupis: 'GTM-TC8ZK7R',
};

/**
 * Объект с языками для формы регистрации на ленде по брендам
 * @type {{zenit: [string], zenbet: [string]}}
 */
export const LANGS_FOR_REGISTRATION = {
  zenit: ['ru', 'kz', 'ua'],
  zenbet: ['br'],
};

/**
 * Функция на проверку нужна ли форма регистрации для локали по бренду
 * @param {String} brandName бренд ленда
 * @param {String} lang язык ленда
 * @returns {Boolean} возвращает true, если для локали бренда нужна форма регистрации
 * и false - если не нужна
 */
export const defineIsRegFormNeeded = (brandName, lang) => {
  if (LANGS_FOR_REGISTRATION[brandName].includes(lang)) {
    return true;
  }
  return false;
};

/**
 * Массив с кириллическими языками (в т.ч. условно кириллическими - 'uz')
 * (например, для вариаций с платёжками в зависимости от языка
 * и для определения языка правил)
 * @type [string, string]
 */
export const LANG_SET_CYRILLIC = ['ru', 'kz', 'ua', 'uz', 'tm'];

/**
 * Функция на проверку входит ли язык ленда в число кириллических языков
 * @param {String} lang язык ленда
 * @returns {Boolean} возвращает true, если язык ленда входит в число кириллических языков
 * и false - если не входит
 */
export const defineIsLangCyrillic = (lang) => {
  if (LANG_SET_CYRILLIC.includes(lang)) {
    return true;
  }
  return false;
};

/**
 * Массив языков с направлением письма справа-налево
 * @type [string, string]
 */
export const LANG_SET_RTL = ['ae', 'ir'];

/**
 * Функция на проверку входит ли язык ленда в число языков с написанием справа налево
 * @param {String} lang язык ленда
 * @returns {Boolean} возвращает true, если язык ленда входит в число
 * языков с написанием справа налево и false - если не входит
 */
export const defineIsDirRTL = (lang) => {
  if (LANG_SET_RTL.includes(lang)) {
    return true;
  }
  return false;
};

/**
 * Функция подбора контактов службы поддержки в зависимости от бренда
 * @param {String} brandName бренд
 * @returns {Boolean} возвращает контакты службы поддержки бренда для вставки в ссылку
 */
export const defineSupportCommunicationMeans = (brandName) => {
  if (brandName === BRANDS.zenit) {
    return 'tel: 88002222212';
  }
  if (brandName === BRANDS.cupis) {
    return 'tel: 88002225550';
  }
  return 'mailto: support@zen.bet';
};

/**
 * Ключи к иконкам соц сетей и ссылки на соц сети по странам
 * @type {{twitter: {br: string, img: string, ru: string, ir: string, en: string, id: number, mx: string, ua: string, kz: string}, vk: {br: string, img: string, ru: string, ir: string, en: string, id: number, mx: string, ua: string, kz: string}, facebook: {br: string, img: string, ru: string, ir: string, en: string, id: number, mx: string, ua: string, kz: string}, telegram: {br: string, img: string, ru: string, ir: string, en: string, id: number, mx: string, ua: string, kz: string}, instagram: {br: string, img: string, ru: string, ir: string, en: string, id: number, mx: string, ua: string, kz: string}}}
 */
export const SOCIAL_LINKS = {
  instagram: {
    id: 0,
    // img: `<svg class="social-links-wrapper__icon" id="instagram" width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    //             <circle cx="20" cy="20" r="20" fill="#929292"/>
    //             <path d="M26.5808 12H14.4192C13.0462 12 12 13.0462 12 14.4192V26.5808C12 27.9538 13.0462 29 14.4192 29H26.5808C27.9538 29 29 27.9538 29 26.5808V14.4192C29 13.0462 27.9538 12 26.5808 12ZM20.5 25.6C23.3115 25.6 25.6 23.3769 25.6 20.6962C25.6 20.2385 25.5346 19.7154 25.4038 19.3231H26.8423V26.2538C26.8423 26.5808 26.5808 26.9077 26.1885 26.9077H14.8115C14.4846 26.9077 14.1577 26.6462 14.1577 26.2538V19.2577H15.6615C15.5308 19.7154 15.4654 20.1731 15.4654 20.6308C15.4 23.3769 17.6885 25.6 20.5 25.6ZM20.5 23.6385C18.6692 23.6385 17.2308 22.2 17.2308 20.4346C17.2308 18.6692 18.6692 17.2308 20.5 17.2308C22.3308 17.2308 23.7692 18.6692 23.7692 20.4346C23.7692 22.2654 22.3308 23.6385 20.5 23.6385ZM26.7769 16.6423C26.7769 17.0346 26.45 17.3615 26.0577 17.3615H24.2269C23.8346 17.3615 23.5077 17.0346 23.5077 16.6423V14.8769C23.5077 14.4846 23.8346 14.1577 24.2269 14.1577H26.0577C26.45 14.1577 26.7769 14.4846 26.7769 14.8769V16.6423Z" fill="white"/>
    //           </svg>`,
    img: `<svg width="40" height="40" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M13.0662 18.0002C13.0662 20.8194 15.3568 22.9339 17.9999 22.9339C20.643 22.9339 22.9336 20.6432 22.9336 18.0002C22.9336 15.1809 20.643 13.0664 17.9999 13.0664C15.1806 13.0664 13.0662 15.3571 13.0662 18.0002Z" fill="white"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M12.009 3.375H23.991C25.5768 3.5512 26.634 3.72741 27.5151 4.07982C28.5723 4.60843 29.2771 4.96084 30.1581 5.84187C31.0392 6.72289 31.5678 7.60392 31.9202 8.48494C32.2726 9.36597 32.625 10.4232 32.625 12.009V23.991C32.4488 25.5768 32.2726 26.634 31.9202 27.5151C31.3916 28.5723 31.0392 29.2771 30.1581 30.1581C29.2771 31.0392 28.3961 31.5678 27.5151 31.9202C26.634 32.2726 25.5768 32.625 23.991 32.625H12.009C10.4232 32.4488 9.36597 32.2726 8.48494 31.9202C7.42771 31.3916 6.72289 31.0392 5.84187 30.1581C4.96084 29.2771 4.43223 28.3961 4.07982 27.5151C3.72741 26.634 3.375 25.5768 3.375 23.991V12.009C3.5512 10.4232 3.72741 9.36597 4.07982 8.48494C4.60843 7.42771 4.96084 6.72289 5.84187 5.84187C6.72289 4.96084 7.60392 4.43223 8.48494 4.07982C9.36597 3.72741 10.4232 3.375 12.009 3.375ZM27.515 10.2472C27.515 11.2203 26.7261 12.0092 25.7529 12.0092C24.7798 12.0092 23.9909 11.2203 23.9909 10.2472C23.9909 9.274 24.7798 8.48511 25.7529 8.48511C26.7261 8.48511 27.515 9.274 27.515 10.2472ZM10.4231 18.0002C10.4231 13.7713 13.771 10.4234 17.9999 10.4234C22.2288 10.4234 25.5767 13.7713 25.5767 18.0002C25.5767 22.2291 22.2288 25.577 17.9999 25.577C13.771 25.577 10.4231 22.2291 10.4231 18.0002Z" fill="white"/>
</svg>`,
    zenit: {
      // ru: `https://www.instagram.com/zenit_bet_official/`,
      ru: `https://www.instagram.com/zenitbetcom/`,
      // ua: 'https://www.instagram.com/zenit_bet_official/',
      // kz: 'https://www.instagram.com/zenit_bet_official/',
    },
    zenbet: {
      br: 'https://www.instagram.com/zenbet.br/',
      en: 'https://www.instagram.com/zen.bet/',
      ir: 'https://www.instagram.com/zenbet_ir/',
      mx: 'https://www.instagram.com/zenbet.mx/',
      ru: 'https://www.instagram.com/zen.bet/',
    },
    cupis: {
      // ru: 'https://www.instagram.com/bkzenit_cupis/'
    },
  },
  vk: {
    id: 1,
    // img: `<svg class="social-links-wrapper__icon" id="vk" width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    //             <circle cx="20" cy="20" r="20" fill="#929292"/>
    //             <path d="M29.8063 25.1153C29.8063 25.0323 29.7232 25.0323 29.7232 24.9493C29.3911 24.2851 28.6439 23.4549 27.6477 22.5416C27.1495 22.1265 26.9004 21.7944 26.7344 21.6284C26.4853 21.2963 26.4853 21.0472 26.5684 20.7151C26.6514 20.466 26.9835 20.0509 27.5646 19.3037C27.8967 18.8886 28.0628 18.6395 28.3118 18.3905C29.5572 16.73 30.1383 15.6507 29.9723 15.1526L29.8893 15.0695C29.8063 14.9865 29.7232 14.9035 29.5572 14.9035C29.3911 14.8205 29.1421 14.8205 28.893 14.9035H25.7381C25.6551 14.9035 25.6551 14.9035 25.4891 14.9035C25.406 14.9035 25.323 14.9035 25.323 14.9035H25.24H25.157L25.0739 14.9865C24.9909 15.0695 24.9909 15.0695 24.9909 15.1526C24.6588 16.0658 24.2437 16.813 23.8286 17.6433C23.5795 18.0584 23.3305 18.4735 23.0814 18.8056C22.8323 19.1377 22.6663 19.3867 22.5002 19.5528C22.3342 19.7188 22.1681 19.8018 22.0851 19.9679C22.0021 20.0509 21.8361 20.1339 21.8361 20.0509C21.753 20.0509 21.67 20.0509 21.67 19.9679C21.587 19.8849 21.504 19.8018 21.4209 19.7188C21.3379 19.6358 21.3379 19.4698 21.2549 19.3037C21.2549 19.1377 21.2549 18.9716 21.2549 18.8886C21.2549 18.8056 21.2549 18.5565 21.2549 18.3905C21.2549 18.1414 21.2549 17.9753 21.2549 17.8923C21.2549 17.6433 21.2549 17.3112 21.2549 16.9791C21.2549 16.647 21.2549 16.3979 21.2549 16.2319C21.2549 16.0658 21.2549 15.8168 21.2549 15.6507C21.2549 15.4016 21.2549 15.2356 21.2549 15.1526C21.2549 15.0695 21.1719 14.9035 21.1719 14.8205C21.0889 14.7375 21.0058 14.6544 20.9228 14.5714C20.8398 14.4884 20.6737 14.4884 20.5907 14.4054C20.1756 14.3223 19.6775 14.2393 19.0963 14.2393C17.7679 14.2393 16.8547 14.3223 16.5226 14.4884C16.3565 14.5714 16.1905 14.6544 16.1075 14.8205C15.9414 14.9865 15.9414 15.0695 16.0245 15.0695C16.4396 15.1526 16.7717 15.3186 16.9377 15.5677L17.0207 15.7337C17.1038 15.8168 17.1037 15.9828 17.1868 16.2319C17.2698 16.4809 17.2698 16.73 17.2698 17.0621C17.2698 17.5602 17.2698 18.0584 17.2698 18.3905C17.2698 18.8056 17.1868 19.0546 17.1868 19.3037C17.1868 19.5528 17.1038 19.7188 17.0207 19.8019C16.9377 19.9679 16.9377 20.0509 16.8547 20.0509C16.8547 20.0509 16.8547 20.1339 16.7717 20.1339C16.6886 20.1339 16.6056 20.217 16.4396 20.217C16.3565 20.217 16.1905 20.1339 16.1075 20.0509C15.9414 19.9679 15.7754 19.8019 15.6924 19.6358C15.5263 19.4698 15.3603 19.2207 15.1942 18.8886C15.0282 18.5565 14.7791 18.2244 14.6131 17.7263L14.447 17.3942C14.364 17.2281 14.198 16.896 14.0319 16.4809C13.8659 16.0658 13.6998 15.7337 13.5338 15.3186C13.4507 15.1526 13.3677 15.0695 13.2847 14.9865H13.2017C13.2017 14.9865 13.1186 14.9035 13.0356 14.9035C12.9526 14.9035 12.8696 14.8205 12.7866 14.8205H9.79773C9.46564 14.8205 9.2996 14.9035 9.21657 14.9865L9.13354 15.0695C9.13354 15.0695 9.13354 15.1526 9.13354 15.2356C9.13354 15.3186 9.13355 15.4016 9.21657 15.5677C9.63169 16.564 10.1298 17.5602 10.628 18.5565C11.1261 19.4698 11.6242 20.3 11.9563 20.8812C12.3714 21.4623 12.7035 22.0435 13.1186 22.5416C13.5338 23.0397 13.7828 23.3718 13.8658 23.5379C14.0319 23.7039 14.1149 23.7869 14.1979 23.87L14.447 24.119C14.6131 24.2851 14.8621 24.5342 15.1942 24.7832C15.5263 25.0323 15.9414 25.2814 16.3565 25.5304C16.7717 25.7795 17.2698 25.9456 17.7679 26.1116C18.3491 26.2776 18.8472 26.3607 19.3454 26.2776H20.5907C20.8398 26.2776 21.0058 26.1946 21.1719 26.0286L21.2549 25.9455C21.2549 25.8625 21.3379 25.8625 21.3379 25.7795C21.3379 25.6965 21.3379 25.6135 21.3379 25.4474C21.3379 25.1153 21.3379 24.8663 21.4209 24.6172C21.504 24.3681 21.504 24.2021 21.587 24.036C21.67 23.87 21.753 23.787 21.8361 23.7039C21.9191 23.6209 22.0021 23.5379 22.0021 23.5379H22.0851C22.2512 23.4549 22.5002 23.5379 22.6663 23.7039C22.9154 23.87 23.1644 24.119 23.3305 24.3681C23.4965 24.6172 23.7456 24.8663 24.0777 25.1983C24.4098 25.5304 24.6588 25.7795 24.8249 25.8625L25.0739 26.0286C25.24 26.1116 25.406 26.1946 25.6551 26.2776C25.9042 26.3607 26.0702 26.3607 26.2363 26.3607L29.0591 26.2776C29.3081 26.2776 29.5572 26.1946 29.7232 26.1116C29.8893 26.0286 29.9723 25.9455 29.9723 25.7795C29.9723 25.6965 29.9723 25.5304 29.9723 25.4474C29.8062 25.2814 29.8063 25.1983 29.8063 25.1153Z" fill="white"/>
    //           </svg>`,
    img: `<svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fillRule="evenodd" clipRule="evenodd" d="M36 18C36 27.9411 27.9411 36 18 36C8.05888 36 0 27.9411 0 18C0 8.05888 8.05888 0 18 0C27.9411 0 36 8.05888 36 18ZM8.99561 12.7725C9.14318 19.8559 12.6848 24.1127 18.8941 24.1127H19.2462V20.0602C21.5278 20.2872 23.2531 21.9559 23.9455 24.1127H27.1695C26.2841 20.8888 23.9569 19.1066 22.5039 18.4256C23.9569 17.5855 26.0002 15.5422 26.4883 12.7725H23.5595C22.9238 15.0201 21.0397 17.0634 19.2462 17.2563V12.7725H16.3173V20.6278C14.5011 20.1737 12.2081 17.9715 12.1059 12.7725H8.99561Z" fill="white"/>
  </svg>`,
    zenit: {
      ru: `https://vk.com/zenitbetofficial`,
      ua: 'https://vk.com/zenitbetofficial',
      kz: 'https://vk.com/zenitbetofficial',
    },
    zenbet: {},
    cupis: {
      ru: 'https://vk.com/zenit_ecupis',
    },
  },
  telegram: {
    id: 2,
    // img: `<svg class="social-links-wrapper__icon" id="telegram" width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    //             <circle cx="20" cy="20" r="20" fill="#929292"/>
    //             <path d="M28.65 12.1052L10.9339 18.6219C9.72482 19.0851 9.73182 19.7285 10.7121 20.0154L15.2605 21.3689L25.7843 15.0352C26.2819 14.7464 26.7365 14.9017 26.3628 15.2182L17.8365 22.5584H17.8345L17.8365 22.5594L17.5227 27.0315C17.9824 27.0315 18.1852 26.8304 18.443 26.5931L20.6523 24.5438L25.2477 27.7817C26.095 28.2268 26.7036 27.998 26.9144 27.0334L29.931 13.472C30.2398 12.2911 29.4584 11.7564 28.65 12.1052V12.1052Z" fill="white"/>
    //           </svg>`,
    img: `<svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fillRule="evenodd" clipRule="evenodd" d="M36 18C36 27.9411 27.9411 36 18 36C8.05887 36 0 27.9411 0 18C0 8.05887 8.05887 0 18 0C27.9411 0 36 8.05887 36 18ZM18.645 13.2884C16.8943 14.0166 13.3952 15.5238 8.14784 17.81C7.29575 18.1488 6.84939 18.4803 6.80875 18.8045C6.74008 19.3522 7.42607 19.568 8.36021 19.8617C8.48727 19.9016 8.61893 19.943 8.75391 19.9869C9.67295 20.2857 10.9092 20.6352 11.5519 20.649C12.1349 20.6616 12.7856 20.4213 13.504 19.928C18.4067 16.6185 20.9376 14.9457 21.0965 14.9097C21.2086 14.8842 21.3639 14.8522 21.4691 14.9458C21.5744 15.0393 21.5641 15.2165 21.5529 15.264C21.4849 15.5537 18.7922 18.0571 17.3987 19.3527C16.9643 19.7565 16.6561 20.043 16.5931 20.1085C16.452 20.255 16.3082 20.3937 16.17 20.5269C15.3161 21.35 14.6759 21.9672 16.2054 22.9752C16.9405 23.4596 17.5286 23.8601 18.1154 24.2597C18.7563 24.6961 19.3954 25.1314 20.2224 25.6735C20.4331 25.8116 20.6343 25.9551 20.8303 26.0948C21.5761 26.6265 22.2461 27.1041 23.0739 27.028C23.5549 26.9837 24.0517 26.5314 24.304 25.1825C24.9003 21.9947 26.0724 15.0877 26.3433 12.2415C26.3671 11.9921 26.3372 11.673 26.3132 11.5329C26.2893 11.3928 26.2392 11.1932 26.0571 11.0454C25.8414 10.8704 25.5085 10.8335 25.3595 10.8361C24.6825 10.8481 23.6438 11.2092 18.645 13.2884Z" fill="white"/>
  </svg>`,
    zenit: {
      ru: `https://t.me/zenit_bet_bk`,
      ua: 'https://t.me/zenit_bet_bk',
      kz: 'https://t.me/zenit_bet_bk',
    },
    zenbet: {
      br: 'https://t.me/zenbetbr',
      en: 'https://t.me/zenbet_official',
      ir: 'https://t.me/zenbetiran',
      mx: 'https://t.me/zenbetmx',
    },
    cupis: {
      ru: 'https://t.me/zenit_cupis',
    },
  },
  facebook: {
    id: 3,
    img: `<svg width="71" height="71" viewBox="0 0 71 71" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g filter="url(#filter0_d_1517_50087)">
            <path d="M35.5002 0C18.1033 0 4 14.1033 4 31.5002C4 47.1032 15.3564 60.0251 30.2466 62.5273V38.072H22.6478V29.2715H30.2466V22.7824C30.2466 15.2531 34.8452 11.1501 41.5628 11.1501C44.7801 11.1501 47.545 11.3899 48.3477 11.4955V19.3655L43.6885 19.3678C40.0361 19.3678 39.3319 21.1031 39.3319 23.6504V29.267H48.0471L46.9104 38.0675H39.3319V62.7377C54.9172 60.8408 67 47.5898 67 31.4912C67 14.1033 52.8967 0 35.5002 0Z" fill="white"/>
            </g>
            <defs>
            <filter id="filter0_d_1517_50087" x="0" y="0" width="71" height="70.7378" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
            <feFlood flood-opacity="0" result="BackgroundImageFix"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dy="4"/>
            <feGaussianBlur stdDeviation="2"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_1517_50087"/>
            <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_1517_50087" result="shape"/>
            </filter>
            </defs>
          </svg>`,
    zenit: {
      // kz: 'https://www.facebook.com/%D0%91%D1%83%D0%BA%D0%BC%D0%B5%D0%BA%D0%B5%D1%80%D1%81%D0%BA%D0%B0%D1%8F-%D0%BA%D0%BE%D0%BD%D1%82%D0%BE%D1%80%D0%B0-%D0%97%D0%B5%D0%BD%D0%B8%D1%82-108983251262959',
      // ru: `https://www.facebook.com/%D0%91%D1%83%D0%BA%D0%BC%D0%B5%D0%BA%D0%B5%D1%80%D1%81%D0%BA%D0%B0%D1%8F-%D0%BA%D0%BE%D0%BD%D1%82%D0%BE%D1%80%D0%B0-%D0%97%D0%B5%D0%BD%D0%B8%D1%82-108983251262959`,
      // ua: 'https://www.facebook.com/%D0%91%D1%83%D0%BA%D0%BC%D0%B5%D0%BA%D0%B5%D1%80%D1%81%D0%BA%D0%B0%D1%8F-%D0%BA%D0%BE%D0%BD%D1%82%D0%BE%D1%80%D0%B0-%D0%97%D0%B5%D0%BD%D0%B8%D1%82-108983251262959',
    },
    zenbet: {
      br: 'https://www.facebook.com/zenbet.brazil/',
      en: 'https://www.facebook.com/Zenbet-108152668012459',
      ir: 'https://www.facebook.com/Zenbet-108152668012459',
      mx: 'https://www.facebook.com/zenbetmx',
      ru: 'https://www.facebook.com/Zenbet-108152668012459',
    },
  },
  twitter: {
    id: 4,
    img: `<svg class="social-links-wrapper__icon" id="twitter" width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="20" cy="20" r="20" fill="#929292"/>
                <path d="M31 14.0165C30.2277 14.3573 29.3981 14.5875 28.526 14.6916C29.4259 14.1554 30.0991 13.3115 30.4201 12.3173C29.5746 12.8174 28.6493 13.1694 27.6844 13.358C27.0355 12.6681 26.176 12.2108 25.2393 12.0571C24.3027 11.9034 23.3413 12.062 22.5044 12.5082C21.6675 12.9544 21.002 13.6632 20.6111 14.5246C20.2203 15.3861 20.1259 16.3519 20.3428 17.2722C18.6296 17.1865 16.9537 16.7431 15.4238 15.9708C13.8939 15.1984 12.5442 14.1143 11.4622 12.7888C11.0923 13.4243 10.8795 14.1611 10.8795 14.9458C10.8791 15.6522 11.0538 16.3477 11.3881 16.9708C11.7224 17.5938 12.206 18.1251 12.7959 18.5174C12.1117 18.4957 11.4427 18.3116 10.8444 17.9804V18.0357C10.8443 19.0264 11.1885 19.9867 11.8185 20.7535C12.4484 21.5203 13.3254 22.0465 14.3006 22.2427C13.666 22.4138 13.0006 22.439 12.3547 22.3164C12.6299 23.1689 13.1658 23.9143 13.8876 24.4484C14.6093 24.9825 15.4807 25.2785 16.3797 25.2949C14.8535 26.4879 12.9687 27.1351 11.0285 27.1322C10.6848 27.1323 10.3413 27.1123 10 27.0724C11.9695 28.3334 14.2621 29.0026 16.6035 29C24.5296 29 28.8626 22.4629 28.8626 16.7933C28.8626 16.6091 28.858 16.423 28.8497 16.2388C29.6925 15.6319 30.42 14.8803 30.9982 14.0193L31 14.0165Z" fill="white"/>
              </svg>`,
    zenbet: {
      br: 'https://t.me/zenbetbr',
      en: 'https://twitter.com/zenbetbr1',
      ir: 'https://twitter.com/zenbetbr1',
      mx: 'https://twitter.com/zenbetes',
    },
  },
};

/**
 * Функция для подстановки перевода в ленды по локалям
 * @param {Object} translationKeys объект с переводом текстовых строк по локалям
 * @param {String} brandName наименование бренда (в translationKeys данный бренд должен быть указан как ключ бренда и содержать ключи локалей с переводами)
 * @param {String} lang язык ленда (в translationKeys данный язык должен быть указан как ключ локали с переводами)
 * @param {String || Object} translationItem ключ элемента для перевода
 * @returns {String || Object} возвращает строку с переводом текста для указанного элемента
 */
export const getLang = (translationKeys, brandName, lang, translationItem) => {
  if (IS_SERVER) return;
  if (!brandName) {
    return translationKeys[lang][translationItem];
  }
  return translationKeys[brandName]?.[lang][translationItem];
};

/**
 * Функция для проверки наличия гет-параметра в урле страницы
 */

export const searchParam = (getParam) => {
  if (!IS_SERVER) {
    const urlString = window.location.href;
    if (urlString) {
      const url = new URL(urlString);
      const desiredParameter = url.searchParams.get(getParam);
      if (desiredParameter) {
        return desiredParameter;
      }
    }
  }
};

/**
 * Функция для проверки наличия гет-параметра zpartner_uid в урле страницы
 */
export const checkPartnerUID = () => {
  if (!IS_SERVER) {
    const url = window.location.href;
    if (url) {
      const allGetParams = url.split('?')[1]?.split('&');
      const resultGetParams = allGetParams?.filter(
        (item) => item?.split('=')[0] === 'zpartner_uid',
      );
      const zpartnerUID = resultGetParams
        ?.find((item, index, arr) => item === arr[arr.length - 1])
        ?.split('=')[1];
      const zpartnerUIDSymbolsValid = /^[a-z0-9]+$/.test(zpartnerUID);

      if (!!zpartnerUID && zpartnerUIDSymbolsValid && zpartnerUID.length <= 36) {
        return zpartnerUID;
      }
    }
  }
};

/**
 * Функция для проверки наличия гет-параметра в урле страницы по названию гет-параметра
 * @param {String} строка с названием гет-параметра
 * @returns {String} возвращает значение гет-параметра
 */
export const checkSpecificGetParam = (string) => {
  if (!IS_SERVER) {
    const url = window.location.href;
    if (url) {
      const allGetParams = url.split('?')[1]?.split('&');
      const resultGetParams = allGetParams?.filter((item) => item?.split('=')[0] === string);
      const resultGetParamValue = resultGetParams
        ?.find((item, index, arr) => item === arr[arr.length - 1])
        ?.split('=')[1];

      if (resultGetParamValue) {
        return resultGetParamValue;
      }
    }
  }
};

/**
 * Функция для подбора url для перехода по клику на кнопку входа на сайт
 * @param {String} brandName наименование бренда
 * @param {String} lang язык ленда
 * @param {Number || String} langId id языка ленда для записи в куку
 * @returns {String} возвращает строку с url в зависимости от бренда и mobile/desktop версии
 */
export const getLoginUrl = (brandName, lang, langId) => {
  if (!IS_SERVER) {
    setCookie('lang', langId);
    return `${getDeviceURL(brandName, lang)}/${!browser().mobile ? '?login_form' : 'login'}`;
  }
};

/**
 * Функция для подбора url для перехода по клику на кнопку регистрации на сайте
 * @param {String} brandName наименование бренда
 * @param {String} lang язык ленда
 * @returns {String} возвращает строку с url в зависимости от бренда и mobile/desktop версии
 */
export const getSignupUrl = (brandName, lang) => {
  if (IS_SERVER) return;
  const urlPopup = brandName === BRANDS.cupis ? '?regForm' : '?reg_form';
  const is_new = searchParam('is_new');
  if (is_new) {
    return `https://m.zenit.win/registration`;
  }
  return `${getDeviceURL(brandName, lang)}/${
    (brandName === BRANDS.zenit || brandName === BRANDS.cupis) && !browser().mobile
      ? urlPopup
      : `registration${brandName !== BRANDS.cupis ? '/phone' : ''}`
  }`;
};

/**
 * Функция для подбора url для перехода по клику на службу поддержки
 * @param {String} brandName наименование бренда
 * @param {String} lang язык ленда
 * @returns {String} возвращает строку с url в зависимости от бренда и mobile/desktop версии
 */
export const getSupportUrl = (brandName, lang) =>
  !IS_SERVER && `${getDeviceURL(brandName, lang)}/company/support`;

/**
 * Функция для подбора url для перехода по клику на обратную связь
 * @param {String} brandName наименование бренда
 * @param {String} lang язык ленда
 * @returns {String} возвращает строку с url в зависимости от бренда и mobile/desktop версии
 */
export const getFeedbackUrl = (brandName, lang) =>
  !IS_SERVER && `${getDeviceURL(brandName, lang)}/feedback`;

/**
 * Функция для подбора url для перехода по клику на игру казино
 * @param {String} brandName наименование бренда
 * @param {String} lang язык ленда
 * @param {Number} number номер игры
 * @returns {String} возвращает строку с url в зависимости от бренда и mobile/desktop версии
 */
export const getCasinoGame = (brandName, lang, number) =>
  !IS_SERVER && `${getDeviceURL(brandName, lang)}/casino/${number}`;

/**
 * Функция для подбора url для восстановления пароля
 * @returns {String} возвращает строку с url
 */
export const getRecoveryUrl = () => (browser().mobile ? '/m/recovery' : '/?pass_forgot');

/**
 * Параметры новой партнерской программы *
 */
const partnerUID = searchParam('zpartner_uid');
const partnerPromoCode = searchParam('promocode');
const promoCode = searchParam('promo_code');
const code = searchParam('code');

/**
 * Функция для подбора url для скачивания мобильного приложения Android
 * @returns {String} возвращает строку с url
 */
export const getAppAndroidUrl = () => {
  const link = `/ajax/mobile_app/download/${promoCode ? `?promo_code=${promoCode}` : ''}${
    code ? `&code%3D${code}` : ''
  }${partnerUID ? `&utm_source=${partnerUID}` : ''}${
    partnerUID && partnerPromoCode ? `&utm_content=${partnerPromoCode}` : ''
  }`;
  return link;
};

/**
 * Функция для подбора url для скачивания TestFlight
 * @returns {String} возвращает строку с url
 */
export const getAppTestFlightUrl = () => {
  const link = `https://apps.apple.com/us/app/testflight/id899247664`;
  return link;
};

/**
 * Функция для подбора url для скачивания мобильного приложения iOS
 * @returns {String} возвращает строку с url
 */
export const getAppIosUrl = () => {
  const link = `https://na7p8.app.goo.gl/?ibi=com.zenitapp&efr=1&ifl=https://testflight.apple.com/join/gtvD8LNI&link=https://zenitbet.com${
    promoCode ? `?promo_code=${promoCode}` : ''
  }${code ? `&code=${code}` : ''}${partnerUID ? `&utm_source=${partnerUID}` : ''}${
    partnerUID && partnerPromoCode ? `&utm_content=${partnerPromoCode}` : ''
  }${code ? `&code=${code}` : ''}`;
  return link;
};
export const getAppIosUrlCupis = () => {
  const link = `https://na7p8.app.goo.gl/?ibi=com.cupiszenitapp&efr=1&ifl=https%3A%2F%2Fapps.apple.com%2Fru%2Fapp%2F%D0%B1%D0%BA-%D0%B7%D0%B5%D0%BD%D0%B8%D1%82%2Fid1579892801${
    promoCode ? `%3Fpromo_code%3D${promoCode}` : ''
  }${code ? `%26code%3D${code}` : ''}&link=https%3A%2F%2Fzenit.win%2F${
    promoCode ? `%3Fpromo_code%3D${promoCode}` : ''
  }${code ? `%26code%3D${code}` : ''}`;
  return link;
};

// export const getAppIosUrlCupis = () => {
//     const link=`https://na7p8.app.goo.gl/?ibi=com.cupiszenitapp&efr=1&ifl=https%3A%2F%2Fapps.apple.com%2Fru%2Fapp%2F%D0%B1%D0%BA-%D0%B7%D0%B5%D0%BD%D0%B8%D1%82%2Fid1579892801%3Fpromo_code%3D${promoCode}&link=https%3A%2F%2Fzenit.win%2F%3Fpromo_code%3D${
//       promoCode}${code? `&code=${code}` : ''}`;
//   return link;
// }

export const getAppIosUrlZenbet = () => {
  const link = `https://zenbet.page.link/?ibi=com.zenbetapp&efr=1&ifl=https://testflight.apple.com/join/5HicMHXy&link=https%3A%2F%2Fzen.bet/${
    partnerUID ? `?utm_source=${partnerUID}` : ''
  }${partnerUID && partnerPromoCode ? `&utm_content=${partnerPromoCode}` : ''}`;
  return link;
};

/**
 * Функция для подбора url для скачивания мобильного приложения iOS
 * @returns {String} возвращает строку с url
 */

export const getAppIosUrlAjax = () => {
  const link = `/ajax/mobile_app/ios/${partnerUID ? `%3Futm_source%3D${partnerUID}` : ''}${
    partnerUID && partnerPromoCode ? `%26utm_content%3D${partnerPromoCode}` : ''
  }${code ? `%3Fcode%3D${code}` : ''}${promoCode ? `%26promo_code%3D${promoCode}` : ''}`;
  return link;
};

/**
 * Функция для подбора url для скачивания приложения Windows
 * @returns {String} возвращает строку с url
 */
export const getAppWindowsUrl = () => `/ajax/windows_app/download`;

/**
 * Функция для подбора url страницы с приложениями
 * @param {String} brandName наименование бренда
 * @param {String} lang язык ленда
 * @param {Number || String} langId id языка ленда для записи в куку
 * @returns {String} возвращает строку с url
 */
export const getAppLandingUrl = (brandName, lang, langId) => {
  setCookie('lang', langId);
  return `${
    brandName === BRANDS.zenbet ? `/${lang}` : ''
  }/mobile_app/android${throwParamsToLink()}`;
};

/**
 * Функция для подбора url страницы с приложением iOS
 * @param {String} brandName наименование бренда
 * @param {String} lang язык ленда
 * @param {Number || String} langId id языка ленда для записи в куку
 * @returns {String} возвращает строку с url
 */
export const getAppIosLandingUrl = (brandName, lang, langId) => {
  setCookie('lang', langId);
  return `${brandName === BRANDS.zenbet ? `/${lang}` : ''}/application/ios${throwParamsToLink()}`;
};

/**
 * Записывать в куки параметр imprint
 *
 * Без него не формируется PHPSESSID, не получается пройти регистрацию
 */

export const getImprint = (imprint) => {
  const data = new FormData();
  data.set('imprint', imprint);
  setCookie('imprint', imprint);
};

/**
 * Получить url девайса и в зависимости от бренда добавить гео
 * @param {String} brandName название бренда
 * @param {String} lang альфа код языка
 * @returns {string}
 */
const getDeviceURL = (brandName, lang) =>
  `${browser().mobile ? '/m' : ''}${brandName === BRANDS.zenbet ? `/${lang}` : ''}`;

/**
 * Существующие на лендингах промокоды с правилами по брендам
 * @type {{tvshows: {promo_code: string, zenit: number, zenbet: number, cupis: number}}
 */
export const PROMO_CODES = {
  welcome25000: {
    promo_code: 'welcome25000NG_sport',
    promoIds: {
      sport: 47775,
      casino: 47891,
    },
  },
  omad500000: {
    promo_code: 'welcome_omad_3steps',
    questId: 386,
  },
  alga20000: {
    promo_code: 'welcome10000KZ_sport',
    promoIds: {
      sport: 47669,
      casino: 47728,
    },
  },
  casino: {
    promo_code: 'casino',
    zenit: 26052,
    zenbet: 26173,
  },
  casino20k: {
    promo_code: 'casino20kfs',
    zenit: 27110,
    zenbet: 27113,
  },
  casino25k: {
    promo_code: 'casino25kfs',
    zenit: 27295,
    zenbet: 27298,
  },
  UA2000: {
    promo_code: 'UA2000',
    zenit: 26072,
  },
  bonus15k: {
    promo_code: 'bonus15k',
    cupis: 26949,
  },
  bonus20k: {
    promo_code: 'bonus20k',
    cupis: 26977,
  },
  bonus30k: {
    promo_code: 'bonus30k',
    zenit: 26127,
    zenbet: 26128,
  },
  bonus35k: {
    promo_code: 'bonus35k',
    zenit: 26134,
    zenbet: 26137,
  },
  tvshows: {
    promo_code: 'tvshows',
    zenit: 26134,
  },
  vozvrat5000: {
    promo_code: 'vozvrat5000',
    zenit: 25831,
  },
  freebet_5000: {
    promo_code: 'freebet_5000',
    zenit: 25820,
  },
  TestSaid: {
    promo_code: 'TestSaid',
    zenit: 27528,
    cupis: 27527,
    zenbet: 27529,
  },
};

/**
 * Объект для поиска CountryData в сторе по текстовому коду названия страны,
 * если текстовый код названия страны не совпадает с языком / props.lang ленда
 * @type {{lang: string, lang: string}}
 */
export const langsForMisleadingCountryTextCodes = {
  bn: 'bd',
  vi: 'vn',
  sw: 'ke',
};

/**
 * Функция определения текстового кода названия страны для поиска CountryData в сторе,
 * если текстовый код названия страны не совпадает с языком / props.lang ленда
 * @param {String} lang альфа код языка
 * @returns {string}
 */
export const defineCountryTextCode = (lang) => {
  let countryTextCode = lang;
  Object.keys(langsForMisleadingCountryTextCodes).find((item) => {
    if (item === lang) {
      countryTextCode = langsForMisleadingCountryTextCodes[lang];
    }
  });
  return countryTextCode;
};

export const htmlLangAttributes = {
  ae: 'ar-ae',
  bn: 'bn',
  br: 'pt-br',
  de: 'de',
  en: 'en',
  fr: 'fr',
  ir: 'fa',
  kz: 'kk',
  mx: 'es-mx',
  ru: 'ru',
  sw: 'sw',
  tm: 'tk',
  tr: 'tr',
  ua: 'uk',
  uz: 'uz',
  vi: 'vi',
};

export const browser = () => {
  if (IS_SERVER) return {};
  let _ua;
  if (!window._ua) {
    _ua = navigator.userAgent.toLowerCase();
  }

  return {
    version: (_ua.match(/.+(?:me|ox|on|rv|it|era|opr|ie)[\/: ]([\d.]+)/) || [0, '0'])[1],
    opera: /opera/i.test(_ua) || /opr/i.test(_ua),
    msie: (/msie/i.test(_ua) && !/opera/i.test(_ua)) || /trident\//i.test(_ua),
    msie6: /msie 6/i.test(_ua) && !/opera/i.test(_ua),
    msie7: /msie 7/i.test(_ua) && !/opera/i.test(_ua),
    msie8: /msie 8/i.test(_ua) && !/opera/i.test(_ua),
    msie9: /msie 9/i.test(_ua) && !/opera/i.test(_ua),
    mozilla: /firefox/i.test(_ua),
    chrome: /chrome/i.test(_ua),
    safari: !/chrome/i.test(_ua) && /webkit|safari|khtml/i.test(_ua),
    iphone: /iphone/i.test(_ua),
    ipod: /ipod/i.test(_ua),
    iphone4: /iphone.*OS 4/i.test(_ua),
    ipod4: /ipod.*OS 4/i.test(_ua),
    ipad: /ipad/i.test(_ua),
    android: /android/i.test(_ua),
    bada: /bada/i.test(_ua),
    mobile: /iphone|ipod|ipad|opera mini|opera mobi|iemobile|android/i.test(_ua),
    msie_mobile: /iemobile/i.test(_ua),
    safari_mobile: /iphone|ipod|ipad/i.test(_ua) && !!_ua.match(/WebKit/i) && !_ua.match(/CriOS/i),
    opera_mobile: /opera mini|opera mobi/i.test(_ua),
    opera_mini: /opera mini/i.test(_ua),
    mac: /mac/i.test(_ua),
    windows: /windows/i.test(_ua),
    search_bot:
      /(yandex|google|stackrambler|aport|slurp|msnbot|bingbot|twitterbot|ia_archiver|facebookexternalhit)/i.test(
        _ua,
      ),
    da: /(da)/i.test(_ua),
    ios: /iP(hone|od|ad)/.test(navigator.platform),
  };
};

// Не факт что все регулярки правильные, скопировал с основного проекта
const PATTERNS = {
  latin: /(?=^[A-Za-z_\-\s']+$)(?=.*[A-Za-z]+)/,
  cyrillic: /(?=^[А-Яа-яёЁ_\-\s']+$)(?=.*[А-Яа-яёЁ]+)/,
  latin_number: /^[A-Za-z0-9-_]+$/,
  cyrillic_number: /(?=^[А-Яа-яёЁ_\-\s'0-9.,/"\\]+$)(?=.*[А-Яа-яёЁ]+)/,
  latin_or_cyrillic: /^(?=.*[A-Za-z])(?=.*[А-Яа-яёЁ]).*$/,
  latin_cyrillic: /(?=^[A-Za-zА-Яа-яёЁ_\-\s']+$)(?=.*[A-Za-zА-Яа-яёЁ]+)/,
  latin_cyrillic_number: /(?=^[A-Za-zА-Яа-яёЁ0-9_\-\s.,/?!']+$)(?=.*[A-Za-zА-Яа-яёЁ0-9]+)/,
  latin_number_symbol: /(?=^[A-Za-z_\-\s'0-9.,/"\\?!]+$)(?=.*[A-Za-z0-9]+)/,
  number: /^[\d]+$/,
  email: /^[a-z'0-9]+([._-][a-z'0-9]+)*@([a-z0-9]+([._-][a-z0-9]+))+$/i,
  emailCps:
    /(^(?!-)[a-zA-Z\d!#$%&'*+/=?^_`{|}~])(\.?[a-zA-Z\d!#$%&'*+/=?^_`{|}~-]){0,63}@((?!-)[A-Za-z0-9-](?!-)[a-zA-Z\d-]{1,63}\.)+([a-zA-Z]{2,}$)/,
  password: /(?=^.{6,30}$)(?![.\t\r\n\f]).*$/,
  phone: /^[+\d]{0,18}$/,
  city: /(?=^[A-Za-zА-Яа-яёЁ0-9.\-\s']+$)(?=.*[A-Za-zА-Яа-яёЁ0-9]+)/,
  promo_code: /^[\dA-Za-z]+$/,
};

export const checkRegx = (type, value) => PATTERNS[type].test(value);

/**
 * Функция для валидации полей
 * @param {String} value значение поля
 * @param {Object | null} validation принимает объект с параметрами которые нужно учитывать при валидации
 * @returns {Object} {{isValid: boolean, type: string}} возвращает объект с параметрами о результе валидации
 * где isValid это значение true в случае успеха и false в случае провала и type задается строкой если есть
 * потребность отследить нужный валидатор (например для вывода сообщения)
 */
export const fieldValidator = (value, validation = null) => {
  const fieldValidation = {
    isValid: true,
    type: 'success',
  };

  if (!validation) {
    return fieldValidation;
  }

  // Проверка на пустоту
  if (validation.required) {
    fieldValidation.isValid = value.trim() !== '' && fieldValidation.isValid;
    fieldValidation.type = fieldValidatorType(fieldValidation.isValid, 'required');
    if (!fieldValidation.isValid) {
      return fieldValidation;
    }
  }

  // Проверка на максимальную длину maxLength передать параметр number кол-во символов
  if (validation.maxLength) {
    fieldValidation.isValid = value.length <= validation.maxLength && fieldValidation.isValid;
    fieldValidation.type = fieldValidatorType(fieldValidation.isValid, 'maxLength');
    if (!fieldValidation.isValid) {
      return fieldValidation;
    }
  }

  // Проверка на минимальную длину minLength передать параметр number кол-во символов
  if (validation.minLength) {
    fieldValidation.isValid = value.length >= validation.minLength && fieldValidation.isValid;
    fieldValidation.type = fieldValidatorType(fieldValidation.isValid, 'minLength');
    if (!fieldValidation.isValid) {
      return fieldValidation;
    }
  }

  // Проверка на паттерн promoCode передать в validation[promoCode]: true
  if (validation.promoCode) {
    fieldValidation.isValid = checkRegx('promo_code', value) && fieldValidation.isValid;
    fieldValidation.type = fieldValidatorType(fieldValidation.isValid, 'promoCode');
    if (!fieldValidation.isValid) {
      return fieldValidation;
    }
  }

  // Проверка на паттерн latinNumber передать в validation[latinNumber]: true
  if (validation.latinNumber) {
    fieldValidation.isValid = checkRegx('latin_number', value) && fieldValidation.isValid;
    fieldValidation.type = fieldValidatorType(fieldValidation.isValid, 'latinNumber');
    if (!fieldValidation.isValid) {
      return fieldValidation;
    }
  }

  // Проверка на паттерн latinNumberSymbol передать в validation[latinNumberSymbol]: true
  if (validation.latinNumberSymbol) {
    fieldValidation.isValid = checkRegx('latin_number_symbol', value) && fieldValidation.isValid;
    fieldValidation.type = fieldValidatorType(fieldValidation.isValid, 'latinNumberSymbol');
    if (!fieldValidation.isValid) {
      return fieldValidation;
    }
  }

  // Проверка на паттерн noCyrillic передать в validation[noCyrillic]: true
  if (validation.noCyrillic) {
    fieldValidation.isValid = !checkRegx('cyrillic', value) && fieldValidation.isValid;
    fieldValidation.type = fieldValidatorType(fieldValidation.isValid, 'noCyrillic');
    if (!fieldValidation.isValid) {
      return fieldValidation;
    }
  }

  // Проверка на паттерн phone передать в validation[phone]: true
  if (validation.phone) {
    fieldValidation.isValid = checkRegx('phone', value) && fieldValidation.isValid;
    fieldValidation.type = fieldValidatorType(fieldValidation.isValid, 'phone');
    if (!fieldValidation.isValid) {
      return fieldValidation;
    }
  }

  // Проверка на паттерн password передать в validation[password]: true
  if (validation.password) {
    fieldValidation.isValid = checkRegx('password', value) && fieldValidation.isValid;
    fieldValidation.type = fieldValidatorType(fieldValidation.isValid, 'password');
    if (!fieldValidation.isValid) {
      return fieldValidation;
    }
  }

  // Проверка на паттерн email cupisIAPI в validation[password]: true
  if (validation.emailCps) {
    fieldValidation.isValid = checkRegx('emailCps', value) && fieldValidation.isValid;
    fieldValidation.type = fieldValidatorType(fieldValidation.isValid, 'emailCps');
    if (!fieldValidation.isValid) {
      return fieldValidation;
    }
  }

  // Проверка на паттерн number передать в validation[number]: true
  if (validation.number) {
    fieldValidation.isValid = checkRegx('number', value) && fieldValidation.isValid;
    fieldValidation.type = fieldValidatorType(fieldValidation.isValid, 'number');
    if (!fieldValidation.isValid) {
      return fieldValidation;
    }
  }

  // Проверка на паттерн promo_code передать в validation[promo_code]: true
  if (validation.promo_code) {
    fieldValidation.isValid = checkRegx('promo_code', value) && fieldValidation.isValid;
    fieldValidation.type = fieldValidatorType(fieldValidation.isValid, 'promo_code');
    if (!fieldValidation.isValid) {
      return fieldValidation;
    }
  }

  return fieldValidation;
};

export const fieldValidatorType = (isValid, type) => (!isValid ? type : 'success');

/**
 * Убрать из номера телефона лишние символы,
 * к примеру: +7(985)801-10-11 привести к виду +79858011011
 * @param {String} value Номер телефона
 */
export const transformPhoneNumber = (value) => value.replace(/[^0-9+]/g, '');

/**
 * Получить ссылку, с коорой совершили переход на лендинг
 */
export const getReferrerUrl = () => {
  if (!IS_SERVER) {
    if (document.referrer) {
      return document.referrer;
    }
    return 'direct';
  }
};

/**
 * Объект с названиями лендингов по типам (
 */

export const LANDING_NAMES_BY_TYPE = {
  another: ['android', 'authorization', 'products', 'withoutoffer', 'applications'],
  casino: [
    'betgamesnew',
    'bonus20000',
    'bonus25000',
    'spin_machine',
    'spin_machine25',
    'slot_machine',
    'slot_machine25',
    'welcome_bonus_casino',
    'welcome_bonus_casino25',
    'welcome_bonus_casino_ny22',
  ],
  tournament: {
    casino: ['casino_13012022', 'GameART_Tournament'],
  },
  // TODO sport (?)
};
